import type { ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { IconHint, IconInstallation, IconLadeboks } from "../../components/icons";
import { useWizard, type Area, type Setup } from "./WizardContext";

const choices: { key: Setup; Ikon: ComponentType<{ className?: string }>; t: string; s: string }[] = [
  { key: "box", Ikon: IconLadeboks, t: "step2.box", s: "step2.boxSub" },
  { key: "none", Ikon: IconInstallation, t: "step2.none", s: "step2.noneSub" },
];

export default function Step2Situation() {
  const { t } = useTranslation();
  const { state, dispatch } = useWizard();
  const hint = state.setup === "box" ? "step2.hintBox" : "step2.hintNone";

  return (
    <div>
      <span className="font-mono text-xs uppercase tracking-[0.14em] text-accent-deep">{t("step2.kicker")}</span>
      <h2 className="mt-2.5 text-[clamp(26px,4.4vw,40px)]">{t("step2.title")}</h2>
      <p className="mb-6 mt-2 max-w-[54ch] text-ink-soft">{t("step2.help")}</p>

      <div className="grid gap-3 sm:grid-cols-2">
        {choices.map(({ key, Ikon, t: navn, s: under }) => {
          const sel = state.setup === key;
          return (
            <button
              key={key}
              type="button"
              aria-pressed={sel}
              onClick={() => dispatch({ type: "setup", value: key })}
              className={`rounded-m border p-4 text-left transition ${
                sel ? "border-ink bg-ink text-canvas-2" : "border-line bg-surface hover:-translate-y-0.5 hover:border-accent-deep"
              }`}
            >
              {/* Ikonplade i samme idiom som logopladen i topbaren. Den valgte
                  plade er stedet accenten faar lov at lyse i dette trin. */}
              <span
                className={`mb-3 grid h-10 w-10 place-items-center rounded-s transition ${
                  sel ? "bg-accent text-accent-ink" : "bg-accent-tint text-accent-deep"
                }`}
              >
                <Ikon className="h-[21px] w-[21px]" />
              </span>
              <b className="block text-[15px]">{t(navn)}</b>
              <small className={sel ? "opacity-70" : "text-ink-faint"}>{t(under)}</small>
            </button>
          );
        })}
      </div>

      <fieldset className="mt-5">
        <legend className="text-[15px] font-semibold">{t("step2.areaLabel")}</legend>
        <p className="mb-2.5 mt-0.5 text-[12.5px] text-ink-faint">{t("step2.areaHelp")}</p>
        <div className="grid gap-3 sm:grid-cols-2">
          {(
            [
              { key: "DK1", t: "step2.west", s: "step2.westSub" },
              { key: "DK2", t: "step2.east", s: "step2.eastSub" },
            ] as { key: Area; t: string; s: string }[]
          ).map((c) => {
            const sel = state.area === c.key;
            return (
              <button
                key={c.key}
                type="button"
                aria-pressed={sel}
                onClick={() => dispatch({ type: "area", value: c.key })}
                className={`rounded-m border p-4 text-left transition ${
                  sel ? "border-ink bg-ink text-canvas-2" : "border-line bg-surface hover:-translate-y-0.5 hover:border-accent-deep"
                }`}
              >
                <b className="block text-[15px]">{t(c.t)}</b>
                <small className={sel ? "opacity-70" : "text-ink-faint"}>{t(c.s)}</small>
              </button>
            );
          })}
        </div>
      </fieldset>

      <label className="mt-4 flex cursor-pointer items-center gap-3 rounded-m border border-line bg-surface px-4 py-3.5">
        <input
          type="checkbox"
          className="h-5 w-5 accent-[var(--accent)]"
          checked={state.night}
          onChange={(e) => dispatch({ type: "night", value: e.target.checked })}
        />
        <span>
          <b className="text-[14.5px]">{t("step2.night")}</b>
          <small className="block text-[12.5px] text-ink-faint">{t("step2.nightSub")}</small>
        </span>
      </label>

      <div className="mt-5 flex gap-3 rounded-m border border-accent bg-accent-tint px-4 py-3.5 text-[14px] text-accent-ink">
        <IconHint className="mt-px h-5 w-5 shrink-0" />
        <span>{t(hint)}</span>
      </div>
    </div>
  );
}
